import { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Copy, Timer, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, money, errMsg, unitPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { NetworkPicker, WalletPanel } from "@/components/store/CryptoPayment";
import { PaymentGuide } from "@/components/store/PaymentGuide";

const field = "w-full h-11 px-3 rounded-md bg-[#08090C] border border-white/10 focus:outline-none focus:border-emerald-500/60";

const Step = ({ n, title, children }) => (
  <section className="rounded-xl border border-white/10 bg-[#0F1117] p-4 sm:p-8 space-y-5 rise">
    <div className="flex items-center gap-3">
      <span className="w-7 h-7 rounded-full bg-emerald-500 text-[#08090C] font-mono font-bold text-sm grid place-items-center">{n}</span>
      <h2 className="font-display font-semibold tracking-tight text-base sm:text-lg">{title}</h2>
    </div>
    {children}
  </section>
);

const Summary = ({ items, total }) => (
  <aside data-testid="checkout-summary" className="order-first lg:order-none rounded-xl border border-white/10 bg-[#0F1117] p-4 sm:p-6 space-y-4 lg:sticky lg:top-24">
    <p className="label-eyebrow text-gray-500">Order summary</p>
    {items.map((i) => (
      <div key={i.id || i.game_id} className="flex justify-between gap-4 text-sm">
        <span>{i.name} <span className="text-gray-500">× {i.qty}</span><br /><span className="text-xs text-gray-500">{i.kind === "box" ? `${i.codes} random codes` : i.platform}</span></span>
        <span className="font-mono">{money(unitPrice(i, i.qty) * i.qty)}</span>
      </div>
    ))}
    <div className="border-t border-white/10 pt-4 flex justify-between">
      <span className="text-gray-400">Total</span>
      <span data-testid="checkout-total" className="font-mono text-xl font-bold text-emerald-400">{money(total)}</span>
    </div>
  </aside>
);

const useCountdown = (expiresAt) => {
  const calc = () => (expiresAt ? Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000)) : 0);
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    setLeft(calc());
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]); // eslint-disable-line react-hooks/exhaustive-deps
  return left;
};

const Countdown = ({ left }) => {
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  const urgent = left <= 60;
  return (
    <div data-testid="payment-countdown" className={`sticky top-16 lg:static z-30 flex items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border backdrop-blur-xl ${urgent ? "border-red-500/50 bg-red-950/80" : "border-amber-500/40 bg-[#1f1708]/90"}`}>
      <div className="flex items-center gap-3">
        <Timer className={`shrink-0 ${urgent ? "text-red-400" : "text-amber-400"}`} size={20} />
        <p className="text-xs sm:text-sm">Send the payment and submit your transaction ID <span className="font-semibold">before the timer ends</span>, or the order will not be counted as paid.</p>
      </div>
      <span data-testid="payment-countdown-time" className={`font-mono text-2xl sm:text-3xl font-bold shrink-0 ${urgent ? "text-red-400" : "text-amber-400"}`}>{mm}:{ss}</span>
    </div>
  );
};

const Success = ({ order }) => (
  <div data-testid="order-success" className="max-w-xl mx-auto px-4 pt-20 text-center space-y-6 rise">
    <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
    <h1 className="font-display font-bold tracking-tight text-3xl">Payment submitted</h1>
    <p className="text-gray-400">We're verifying your transaction. Your activation code{order.items.length > 1 ? "s" : ""} will be emailed to <span className="text-white">{order.email}</span> once confirmed.</p>
    <div className="rounded-lg border border-white/10 bg-[#0F1117] p-5">
      <p className="label-eyebrow text-gray-500">Your order ID</p>
      <div className="flex items-center justify-center gap-3 mt-2">
        <span data-testid="order-id" className="font-mono text-2xl font-bold">{order.id}</span>
        <button data-testid="copy-order-id-button" onClick={() => { navigator.clipboard.writeText(order.id); toast.success("Order ID copied"); }} className="text-gray-400 hover:text-emerald-400"><Copy size={16} /></button>
      </div>
      <p className="text-xs text-gray-500 mt-2">Save this to track your order.</p>
    </div>
    <Link to={`/order?id=${order.id}&email=${encodeURIComponent(order.email)}`} data-testid="track-order-link" className="inline-flex h-11 px-6 items-center rounded-md bg-emerald-500 text-[#08090C] font-bold hover:bg-emerald-400 transition-colors">Track order</Link>
  </div>
);

const Expired = ({ onRestart }) => (
  <div data-testid="payment-expired" className="max-w-xl mx-auto px-4 pt-20 text-center space-y-6 rise">
    <XCircle size={56} className="mx-auto text-red-400" />
    <h1 className="font-display font-bold tracking-tight text-3xl">Payment window closed</h1>
    <p className="text-gray-400">The 5-minute window ended before a transaction ID was submitted, so this order was not counted as paid. If you already sent USDT, open the chat and send us your transaction ID.</p>
    <button data-testid="payment-restart-button" onClick={onRestart} className="h-11 px-6 rounded-md bg-emerald-500 text-[#08090C] font-bold hover:bg-emerald-400">Start a new payment</button>
  </div>
);

const PayStep = ({ order, wallet, onDone, onExpired }) => {
  const left = useCountdown(order.expires_at);
  const [tx, setTx] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (left === 0) {
      api.post(`/orders/${order.id}/cancel`, { email: order.email }).catch(() => {});
      onExpired();
    }
  }, [left]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/orders/${order.id}/confirm`, { email: order.email, tx_hash: tx });
      onDone(r.data);
    } catch (err) {
      if (err?.response?.status === 410) onExpired();
      else toast.error(errMsg(err, "Could not submit transaction"));
    } finally { setBusy(false); }
  };
  return (
    <>
      <Countdown left={left} />
      <Step n="2" title="Send the payment"><WalletPanel wallet={wallet} amount={order.total.toFixed(2)} /><div className="mt-4"><PaymentGuide /></div></Step>
      <Step n="3" title="Submit your transaction ID">
        <form onSubmit={submit} className="grid gap-4">
          <input data-testid="tx-hash-input" required minLength={6} value={tx} onChange={(e) => setTx(e.target.value)} placeholder="Paste the transaction hash from your wallet" className={`${field} font-mono text-sm`} />
          <button data-testid="submit-payment-button" disabled={busy || left === 0}
            className="h-12 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-400 disabled:opacity-40 transition-colors">
            {busy && <Loader2 size={16} className="animate-spin" />} I've sent the payment
          </button>
          <p className="text-xs text-gray-500">Order <span className="font-mono">{order.id}</span> · the keys are reserved for you until the timer ends.</p>
        </form>
      </Step>
    </>
  );
};

export default function Checkout() {
  useSEO({ title: "Checkout — Pay with USDT", path: "/checkout" });
  const { items, total, clear } = useCart();
  const [wallets, setWallets] = useState(null);
  const [network, setNetwork] = useState(null);
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [order, setOrder] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    api.get("/wallets").then((r) => { setWallets(r.data); if (r.data[0]) setNetwork(r.data[0].network); });
  }, []);

  if (order) return <Success order={order} />;
  if (expired) return <Expired onRestart={() => { setExpired(false); setSession(null); }} />;
  if (!items.length) return (
    <div data-testid="checkout-empty" className="text-center py-32 space-y-4">
      <p className="text-gray-400">Your cart is empty.</p>
      <Link to="/" className="text-emerald-400 hover:underline">Browse games</Link>
    </div>
  );

  const wallet = wallets?.find((w) => w.network === (session?.network || network));
  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/orders/start", { email, network, items: items.map((i) => ({ game_id: i.id, qty: i.qty })) });
      setSession(r.data);
    } catch (err) { toast.error(errMsg(err, "Could not start payment")); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10 pb-24">
      <p className="label-eyebrow text-emerald-400">Checkout</p>
      <h1 className="font-display font-bold tracking-tight text-2xl sm:text-4xl mt-2 mb-6 sm:mb-8">Pay with USDT</h1>
      <div className="grid lg:grid-cols-[1fr_340px] gap-4 sm:gap-6 items-start">
        <div className="space-y-4 sm:space-y-6 min-w-0">
          {!session ? (
            <Step n="1" title="Choose a network and your email">
              {wallets && wallets.length === 0
                ? <p data-testid="no-wallets-message" className="text-amber-400 text-sm">Crypto payments are being set up. Please check back shortly.</p>
                : <NetworkPicker wallets={wallets || []} value={network} onChange={setNetwork} />}
              <form onSubmit={start} className="grid gap-4">
                <label className="space-y-2 block">
                  <span className="text-sm text-gray-300">Email for your activation code</span>
                  <input data-testid="customer-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={field} />
                </label>
                <p className="text-xs text-gray-500">By continuing you agree to our <Link to="/policies" data-testid="checkout-policies-link" className="underline hover:text-white">policies</Link>: all sales are final, and fake transaction hashes get banned.</p>
                <p className="text-xs text-gray-400">After you continue, you'll have <span className="text-amber-400 font-semibold">5 minutes</span> to send USDT and paste your transaction ID on this page.</p>
                <button data-testid="start-payment-button" disabled={busy || !wallet}
                  className="h-12 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-400 disabled:opacity-40 transition-colors">
                  {busy && <Loader2 size={16} className="animate-spin" />} Continue to payment
                </button>
              </form>
            </Step>
          ) : (
            <PayStep order={session} wallet={wallet} onDone={(o) => { setOrder(o); clear(); }} onExpired={() => setExpired(true)} />
          )}
        </div>
        <Summary items={session ? session.items : items} total={session ? session.total : total} />
      </div>
    </div>
  );
}
